import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { IconPlus, IconSave, IconTrash } from "@/components/SvoIcons";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import type { Topic } from "@offsite/shared";

export function AdminTopicsSection() {
  const { state, dispatch } = useStore();
  const { preparedTopics } = useOffsiteConfig();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Topic | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const reload = async () => {
    const r = await api.topics.list(state.auth.token);
    setTopics(r.topics);
    dispatch({ type: "topics/set", topics: r.topics });
    setDraft({});
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (id: string, title: string) => {
    setDraft((d) => ({ ...d, [id]: title }));
  };

  const save = async (t: Topic) => {
    const next = (draft[t.id] ?? t.title).trim();
    if (!next || next === t.title) return;
    setBusy(t.id);
    setError(null);
    try {
      await api.admin.updateTopic(state.auth.token, t.id, next);
      await reload();
    } catch (err) {
      console.error(err);
      setError(`Konnte Thema ${t.id} nicht speichern.`);
    } finally {
      setBusy(null);
    }
  };

  const doRemove = async (t: Topic) => {
    setConfirmDelete(null);
    setBusy(t.id);
    setError(null);
    try {
      await api.admin.deleteTopic(state.auth.token, t.id);
      await reload();
    } catch (err) {
      console.error(err);
      setError("Konnte Thema nicht löschen.");
    } finally {
      setBusy(null);
    }
  };

  const add = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy("__add");
    setError(null);
    try {
      await api.admin.addTopic(state.auth.token, title);
      setNewTitle("");
      await reload();
    } catch (err) {
      console.error(err);
      setError("Konnte Thema nicht anlegen.");
    } finally {
      setBusy(null);
    }
  };

  // Vorbereitetes Thema per Klick anlegen — vor Publikum tippt niemand gern.
  const addPrepared = async (title: string) => {
    setBusy("__add");
    setError(null);
    try {
      await api.admin.addTopic(state.auth.token, title);
      await reload();
    } catch (err) {
      console.error(err);
      setError("Konnte Thema nicht anlegen.");
    } finally {
      setBusy(null);
    }
  };

  const doResetTopicsAndVotes = async () => {
    setConfirmReset(false);
    setBusy("__reset");
    setError(null);
    try {
      await api.admin.reset(state.auth.token, "topics");
      await api.admin.reset(state.auth.token, "votes");
      await reload();
    } catch (err) {
      console.error(err);
      setError("Reset fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="admin-grid">
      <header className="admin-section-head">
        <h2>Themen verwalten</h2>
        <p>
          Themen anlegen, umbenennen, löschen — wirkt sofort auch auf Presenter- und
          Teilnehmer-Ansicht.
        </p>
      </header>

      {error && <div className="admin-callout">{error}</div>}

      <div className="admin-toolbar">
        <button
          type="button"
          className="btn btn-warning"
          onClick={() => setConfirmReset(true)}
          disabled={busy === "__reset"}
        >
          ↻ Themen + Votes zurücksetzen
        </button>
        <span className="form-hint" style={{ margin: 0 }}>
          Archiviert alle Themen und Stimmen, danach leere Liste.
        </span>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {topics.length === 0 && (
          <p className="form-hint">
            Noch keine Themen angelegt — füge unten welche hinzu.
          </p>
        )}
        {topics.map((t, i) => {
          const value = draft[t.id] ?? t.title;
          const dirty = value.trim() !== t.title;
          const isBusy = busy === t.id;
          return (
            <div key={t.id} className="admin-row">
              <div className="badge">{i + 1}</div>
              <input
                className="input-text"
                value={value}
                onChange={(e) => update(t.id, e.target.value)}
              />
              <button
                type="button"
                onClick={() => save(t)}
                disabled={!dirty || isBusy}
                className="btn btn-ghost"
              >
                <IconSave size={16} />
                {isBusy ? "…" : "Speichern"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(t)}
                disabled={isBusy}
                className="btn btn-danger"
              >
                <IconTrash size={16} />
                Löschen
              </button>
              <code className="topic-id">{t.id}</code>
            </div>
          );
        })}
      </div>

      {preparedTopics && preparedTopics.length > 0 && (
        <div
          className="admin-toolbar"
          style={{
            padding: "14px 16px",
            background: "var(--svo-bg)",
            borderRadius: "var(--radius-xl)",
            flexWrap: "wrap",
          }}
        >
          <span className="form-hint" style={{ margin: 0, width: "100%" }}>
            Vorbereitete Themen — ein Klick legt sie an:
          </span>
          {preparedTopics.map((p) => {
            const exists = topics.some((t) => t.title === p.short);
            return (
              <button
                key={p.short}
                type="button"
                className="btn btn-ghost"
                onClick={() => addPrepared(p.short)}
                disabled={exists || busy === "__add"}
                title={p.full}
              >
                <IconPlus size={16} />
                {p.short}
                {exists ? " ✓" : ""}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="admin-toolbar"
        style={{
          padding: "14px 16px",
          background: "var(--svo-bg)",
          borderRadius: "var(--radius-xl)",
        }}
      >
        <input
          className="input-text grow"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Neues Thema (Enter zum Hinzufügen)"
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
          }}
        />
        <button
          type="button"
          onClick={add}
          disabled={!newTitle.trim() || busy === "__add"}
          className="btn btn-primary"
        >
          <IconPlus size={16} />
          Thema hinzufügen
        </button>
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Thema löschen?"
        message={
          confirmDelete
            ? `„${confirmDelete.title}" wird gelöscht. Alle dafür abgegebenen Stimmen werden ebenfalls entfernt.`
            : ""
        }
        confirmLabel="Ja, löschen"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={() => confirmDelete && doRemove(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        busy={busy === confirmDelete?.id}
      />

      <ConfirmDialog
        open={confirmReset}
        title="Themen + Votes zurücksetzen?"
        message="Alle Themen UND alle Stimmen werden ins Archiv verschoben. Du startest mit einer leeren Themen-Liste neu."
        confirmLabel="Ja, archivieren"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doResetTopicsAndVotes}
        onCancel={() => setConfirmReset(false)}
        busy={busy === "__reset"}
      />
    </div>
  );
}
